import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';

type StateSetterValue<T> = T | ((prev: T) => T);

interface MeetOverlaysState {
  openSettings: boolean;
  showBackgroundModal: boolean;
  showModalInstruct: string | null;
  showScreenShareConflictModal: boolean;
  macDeckModalOpen: boolean;
}

type MeetOverlaysAction =
  | { type: 'setOpenSettings'; payload: boolean }
  | { type: 'setShowBackgroundModal'; payload: boolean }
  | { type: 'setShowModalInstruct'; payload: string | null }
  | { type: 'setShowScreenShareConflictModal'; payload: boolean }
  | { type: 'setMacDeckModalOpen'; payload: boolean };

export interface MeetOverlaysBridgeApi {
  setOpenSettings: (value: StateSetterValue<boolean>) => void;
  setShowBackgroundModal: (value: StateSetterValue<boolean>) => void;
  setShowModalInstruct: (value: StateSetterValue<string | null>) => void;
  setShowScreenShareConflictModal: (value: StateSetterValue<boolean>) => void;
  setMacDeckModalOpen: (value: StateSetterValue<boolean>) => void;
}

interface MeetOverlaysContextValue extends MeetOverlaysState, MeetOverlaysBridgeApi {}

interface MeetOverlaysProviderProps {
  onApi?: (api: MeetOverlaysBridgeApi) => void;
}

const initialState: MeetOverlaysState = {
  openSettings: false,
  showBackgroundModal: false,
  showModalInstruct: null,
  showScreenShareConflictModal: false,
  macDeckModalOpen: false,
};

const MeetOverlaysContext = createContext<MeetOverlaysContextValue | null>(null);

function meetOverlaysReducer(state: MeetOverlaysState, action: MeetOverlaysAction): MeetOverlaysState {
  switch (action.type) {
    case 'setOpenSettings':
      return { ...state, openSettings: action.payload };
    case 'setShowBackgroundModal':
      return { ...state, showBackgroundModal: action.payload };
    case 'setShowModalInstruct':
      return { ...state, showModalInstruct: action.payload };
    case 'setShowScreenShareConflictModal':
      return { ...state, showScreenShareConflictModal: action.payload };
    case 'setMacDeckModalOpen':
      return { ...state, macDeckModalOpen: action.payload };
    default:
      return state;
  }
}

function resolveNextState<T>(value: StateSetterValue<T>, current: T): T {
  return typeof value === 'function' ? (value as (prev: T) => T)(current) : value;
}

export function MeetOverlaysProvider({ children, onApi }: PropsWithChildren<MeetOverlaysProviderProps>) {
  const [state, dispatch] = useReducer(meetOverlaysReducer, initialState);

  const setOpenSettings = useCallback((value: StateSetterValue<boolean>) => {
    dispatch({ type: 'setOpenSettings', payload: resolveNextState(value, state.openSettings) });
  }, [state.openSettings]);

  const setShowBackgroundModal = useCallback((value: StateSetterValue<boolean>) => {
    dispatch({ type: 'setShowBackgroundModal', payload: resolveNextState(value, state.showBackgroundModal) });
  }, [state.showBackgroundModal]);

  const setShowModalInstruct = useCallback((value: StateSetterValue<string | null>) => {
    dispatch({ type: 'setShowModalInstruct', payload: resolveNextState(value, state.showModalInstruct) });
  }, [state.showModalInstruct]);

  const setShowScreenShareConflictModal = useCallback((value: StateSetterValue<boolean>) => {
    dispatch({
      type: 'setShowScreenShareConflictModal',
      payload: resolveNextState(value, state.showScreenShareConflictModal),
    });
  }, [state.showScreenShareConflictModal]);

  const setMacDeckModalOpen = useCallback((value: StateSetterValue<boolean>) => {
    dispatch({ type: 'setMacDeckModalOpen', payload: resolveNextState(value, state.macDeckModalOpen) });
  }, [state.macDeckModalOpen]);

  const value = useMemo<MeetOverlaysContextValue>(() => ({
    ...state,
    setOpenSettings,
    setShowBackgroundModal,
    setShowModalInstruct,
    setShowScreenShareConflictModal,
    setMacDeckModalOpen,
  }), [
    setMacDeckModalOpen,
    setOpenSettings,
    setShowBackgroundModal,
    setShowModalInstruct,
    setShowScreenShareConflictModal,
    state,
  ]);

  useEffect(() => {
    onApi?.({
      setOpenSettings,
      setShowBackgroundModal,
      setShowModalInstruct,
      setShowScreenShareConflictModal,
      setMacDeckModalOpen,
    });
  }, [
    onApi,
    setMacDeckModalOpen,
    setOpenSettings,
    setShowBackgroundModal,
    setShowModalInstruct,
    setShowScreenShareConflictModal,
  ]);

  return <MeetOverlaysContext.Provider value={value}>{children}</MeetOverlaysContext.Provider>;
}

export function useMeetOverlays() {
  const context = useContext(MeetOverlaysContext);

  if (!context) {
    throw new Error('useMeetOverlays must be used within MeetOverlaysProvider');
  }

  return context;
}
